import { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search, ChevronsUpDown, X, Check } from "lucide-react";
import { displayName, initials, matchesSearch, compareAthletes } from "@/lib/athleteName";

/**
 * AthleteSelect — selettore atleta universale CSP Lab (selezione singola, tap-to-pick).
 * Sostituisce i dropdown lunghi: ricerca immediata (cognome/nome), ordine alfabetico
 * Cognome → Nome, filtro per gruppo + ricerca globale, mobile/tablet/desktop.
 * Un atleta multi-gruppo resta lo stesso athlete.id (nessuna duplicazione dati).
 *
 * Props:
 *  - value / onChange(id): id atleta selezionato (controllato)
 *  - athletes / groups (opzionali): se non forniti, li carica da /athletes e /groups
 *  - excludeIds: id da nascondere
 *  - activeOnly (default true): mostra solo atleti attivi (archived != true)
 *  - placeholder, testid, disabled, allowClear, triggerClassName
 */
export function AthleteSelect({
  value,
  onChange,
  athletes: athletesProp,
  groups: groupsProp,
  excludeIds = [],
  activeOnly = true,
  placeholder = "Seleziona atleta",
  testid = "athlete-select",
  disabled = false,
  allowClear = false,
  triggerClassName = "",
}) {
  const [open, setOpen] = useState(false);
  const [athletes, setAthletes] = useState(athletesProp || null);
  const [groups, setGroups] = useState(groupsProp || null);

  useEffect(() => { if (athletesProp) setAthletes(athletesProp); }, [athletesProp]);
  useEffect(() => { if (groupsProp) setGroups(groupsProp); }, [groupsProp]);

  const ensureData = useCallback(async () => {
    if (!athletesProp && athletes === null) {
      try { const { data } = await api.get("/athletes"); setAthletes(data || []); }
      catch { setAthletes([]); }
    }
    if (!groupsProp && groups === null) {
      try { const { data } = await api.get("/groups"); setGroups(data || []); }
      catch { setGroups([]); }
    }
  }, [athletesProp, groupsProp, athletes, groups]);

  useEffect(() => { if (open) ensureData(); }, [open, ensureData]);

  const chosen = useMemo(
    () => (athletes || []).find((a) => a.id === value) || null,
    [athletes, value]
  );
  const label = chosen ? displayName(chosen) : "";

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        data-testid={`${testid}-trigger`}
        onClick={() => setOpen(true)}
        className={`w-full flex items-center gap-2 h-11 px-3 rounded-md border border-zinc-300 bg-white text-left text-sm transition hover:border-zinc-500 disabled:opacity-60 ${triggerClassName}`}
      >
        <span className={`flex-1 truncate ${chosen ? "text-zinc-900" : "text-zinc-400"}`}>
          {label || placeholder}
        </span>
        {allowClear && chosen ? (
          <span
            role="button"
            tabIndex={0}
            data-testid={`${testid}-clear`}
            onClick={(e) => { e.stopPropagation(); onChange(""); }}
            className="p-0.5 rounded hover:bg-zinc-100"
          ><X className="w-4 h-4 text-zinc-400" /></span>
        ) : (
          <ChevronsUpDown className="w-4 h-4 text-zinc-400 shrink-0" />
        )}
      </button>

      <AthleteSelectDialog
        open={open}
        onOpenChange={setOpen}
        athletes={athletes}
        groups={groups}
        excludeIds={excludeIds}
        activeOnly={activeOnly}
        value={value}
        onPick={(id) => { onChange(id); setOpen(false); }}
        testid={testid}
      />
    </>
  );
}

/**
 * AthleteSelectDialog — corpo riusabile a comparsa (es. "Aggiungi atleta" nelle presenze).
 */
export function AthleteSelectDialog({
  open,
  onOpenChange,
  athletes,
  groups,
  excludeIds = [],
  activeOnly = true,
  value = null,
  onPick,
  title = "Seleziona atleta",
  testid = "athlete-select",
}) {
  const [q, setQ] = useState("");
  const [groupId, setGroupId] = useState("all");

  useEffect(() => { if (open) { setQ(""); setGroupId("all"); } }, [open]);

  const excluded = useMemo(() => new Set(excludeIds), [excludeIds]);
  const athleteGroups = (a) => a.group_ids || (a.group_id ? [a.group_id] : []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (athletes || [])
      .filter((a) => !excluded.has(a.id))
      .filter((a) => (activeOnly ? (a.active !== false && !a.archived) : true))
      .filter((a) => (groupId === "all" ? true : athleteGroups(a).includes(groupId)))
      .filter((a) => matchesSearch(a, term))
      .sort(compareAthletes);
  }, [athletes, q, groupId, excluded, activeOnly]);

  const usableGroups = groups || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`${testid}-dialog`}
        className="max-w-lg p-0 gap-0 max-h-[88vh] flex flex-col overflow-hidden"
      >
        <DialogHeader className="px-4 pt-4 pb-2 text-left">
          <DialogTitle className="text-base">{title}</DialogTitle>
          <DialogDescription className="sr-only">Cerca e seleziona un atleta per cognome o nome, con filtro per gruppo.</DialogDescription>
        </DialogHeader>

        {/* Ricerca globale (indipendente dal gruppo) */}
        <div className="px-4 pb-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              autoFocus
              data-testid={`${testid}-search`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per cognome o nome…"
              className="h-12 pl-9 text-base"
            />
          </div>
        </div>

        {/* Filtro per gruppo */}
        {usableGroups.length > 0 && (
          <div className="px-4 pb-2 flex items-center gap-1.5 overflow-x-auto no-scrollbar" data-testid={`${testid}-groups`}>
            <GroupChip active={groupId === "all"} onClick={() => setGroupId("all")} testid={`${testid}-group-all`}>Tutti</GroupChip>
            {usableGroups.map((g) => (
              <GroupChip key={g.id} active={groupId === g.id} onClick={() => setGroupId(g.id)} testid={`${testid}-group-${g.id}`}>
                {g.name}
              </GroupChip>
            ))}
          </div>
        )}

        {/* Lista atleti */}
        <div className="flex-1 overflow-y-auto border-t border-zinc-100 min-h-[200px]">
          {athletes === null ? (
            <div className="p-6 text-sm text-zinc-400 text-center">Caricamento…</div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-sm text-zinc-500 text-center" data-testid={`${testid}-empty`}>Nessun atleta trovato.</div>
          ) : (
            <div className="divide-y divide-zinc-100">
              {filtered.map((a) => {
                const isSel = a.id === value;
                return (
                  <button
                    key={a.id}
                    type="button"
                    data-testid={`${testid}-option-${a.id}`}
                    onClick={() => onPick(a.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left transition ${isSel ? "bg-zinc-100" : "hover:bg-zinc-50"}`}
                  >
                    <div className="w-9 h-9 rounded-full bg-zinc-100 flex items-center justify-center text-sm font-semibold text-zinc-600 shrink-0">
                      {initials(a)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{displayName(a)}</div>
                      <div className="text-xs text-zinc-500 truncate">
                        {(a.weapon || (a.weapons && a.weapons[0]?.weapon_code) || "—")}{a.category ? ` · ${a.category}` : ""}
                      </div>
                    </div>
                    {isSel && <Check className="w-4 h-4 text-[#0052FF] shrink-0" />}
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="px-4 py-2 border-t border-zinc-100 text-[11px] text-zinc-400 shrink-0">
          {filtered.length} atleti · ordine Cognome → Nome
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GroupChip({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`shrink-0 text-xs px-3 py-1.5 rounded-full border transition ${
        active ? "bg-black text-white border-black" : "bg-white text-zinc-600 border-zinc-300 hover:border-zinc-500"
      }`}
    >{children}</button>
  );
}

export default AthleteSelect;
